import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqs: FAQItem[] = [
  {
    question: 'What is VigorBuy Spreadsheet?',
    answer: 'VigorBuy Spreadsheet is an organized product discovery database that consolidates thousands of curated products into a single, filterable spreadsheet. It includes detailed product information, pricing, category tags, and QC photos — making it dramatically faster and easier to find, compare, and purchase products compared to traditional browsing methods. The spreadsheet is regularly updated with new finds and trending items.',
  },
  {
    question: 'Is Kakobuy Spreadsheet safe to use?',
    answer: 'Yes, Kakobuy Spreadsheet is safe when accessed through official channels. The spreadsheet itself is a product information database and does not store any personal or payment data. To stay safe, always verify you are using the authentic spreadsheet source, review QC photos before purchasing, use secure payment methods, and never share your account credentials. Following these basic precautions ensures a secure shopping experience.',
  },
  {
    question: 'How do I use a shopping spreadsheet?',
    answer: 'Using a shopping spreadsheet is straightforward. Open the spreadsheet and browse through the organized categories. Each row represents a product with columns for name, price, images, and QC photos. You can filter by category, sort by price, and compare products side by side. When you find something you like, simply follow the product link to the shopping platform. The structured format makes it easy to track and compare multiple items at once.',
  },
  {
    question: 'Where can I find the latest product lists?',
    answer: 'The latest product lists are available right here on VigorBuy Spreadsheet. Browse our category sections above to access organized product collections including Shoes, Hoodies, T-Shirts, Jackets, Accessories, and many more. Each category links directly to the corresponding product spreadsheet database, ensuring you always have access to the most current product information available.',
  },
  {
    question: 'How often are spreadsheets updated?',
    answer: 'The VigorBuy Spreadsheet database is updated on a regular basis to include new product discoveries, trending items, and seasonal collections. Product listings, prices, and availability are refreshed to ensure accuracy. The frequency of updates varies by category — high-demand categories like sneakers and fashion may see more frequent refreshes, while other categories maintain consistent updates to keep the database current and reliable.',
  },
  {
    question: 'What is the difference between VigorBuy and Kakobuy spreadsheets?',
    answer: 'Both VigorBuy Spreadsheet and Kakobuy Spreadsheet are product discovery databases, but they may differ in their product focus, organization style, and update frequency. VigorBuy Spreadsheet offers a comprehensive product database with detailed categorization, QC photo integration, and shopping guides. Kakobuy Spreadsheet provides an alternative product discovery method with its own unique product curation. Using both can give you a wider range of product options and comparison opportunities.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-16 md:py-20 bg-background-50">
      <div className="max-w-[960px] mx-auto px-4 md:px-6 lg:px-10">
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
            Common Questions
          </span>
          <h2 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            Everything you need to know about spreadsheet shopping
          </p>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer hover:bg-background-50/50 transition-colors"
              >
                <h3 className="font-heading text-sm md:text-base text-foreground-900 pr-4">{faq.question}</h3>
                <div className={`w-6 h-6 flex items-center justify-center transition-transform duration-300 flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-lg text-foreground-400"></i>
                </div>
              </button>
              <div
                className={`transition-all duration-300 overflow-hidden ${
                  openIndex === index ? 'max-h-96 pb-5 md:pb-6 px-5 md:px-6' : 'max-h-0'
                }`}
              >
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}